import { createUseStyles } from 'react-jss';

import { ThemeProps } from '@styles/theme';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
        paddingTop: theme.spacing(2),
    },

    topBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },

    topBarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },

    heroAllSessionsBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: `6px 16px`,
        borderRadius: 999,
        border: '1.5px solid rgba(255, 255, 255, 0.55)',
        background: 'rgba(255, 255, 255, 0.14)',
        backdropFilter: 'blur(8px)',
        color: theme.colors.white,
        fontFamily: theme.font.secondary,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.03em',
        cursor: 'pointer',
        flexShrink: 0,
        alignSelf: 'flex-start',
        transition: 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.26)',
            borderColor: 'rgba(255, 255, 255, 0.8)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
        },
    },

    backButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: 999,
        background: theme.colors.white,
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
        fontSize: 13,
        fontWeight: 700,
        padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
        cursor: 'pointer',
        transition: 'border-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
        boxShadow: '0 4px 12px rgba(39, 31, 77, 0.06)',
        '&:hover': {
            borderColor: theme.colors.violet_primary,
            color: theme.colors.violet_primary,
            boxShadow: '0 6px 16px rgba(39, 31, 77, 0.12)',
        },
    },

    pageTitle: {
        fontSize: 15,
        fontWeight: 700,
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
    },

    topBarActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },

    refreshMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },

    refreshMetaLabel: {
        fontSize: 12,
        fontWeight: 700,
        color: theme.colors.grey_text,
        letterSpacing: '0.02em',
    },

    refreshButton: {
        minWidth: 110,
    },

    viewSessionsButton: {
        minHeight: 38,
        padding: `0 ${theme.spacing(2)}`,
    },

    hero: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: theme.radius,
        padding: theme.spacing(5),
        background: theme.isDark
            ? `linear-gradient(140deg, #0a0618 0%, #1e1545 42%, #071830 100%)`
            : `linear-gradient(140deg, ${theme.colors.deep_blue} 0%, #6d3fc0 42%, ${theme.colors.blue_primary} 100%)`,
        boxShadow: theme.isDark
            ? '0 26px 60px rgba(0, 0, 0, 0.45)'
            : '0 26px 60px rgba(23, 19, 56, 0.18)',
        color: '#ffffff',
        [theme.down('desktop')]: {
            padding: theme.spacing(4),
        },
    },

    heroGlow: {
        position: 'absolute',
        inset: 'auto -10% -45% auto',
        width: 280,
        height: 280,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 72%)',
        pointerEvents: 'none',
    },

    heroGlowSecondary: {
        position: 'absolute',
        inset: '-30% auto auto 5%',
        width: 200,
        height: 200,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 70%)',
        pointerEvents: 'none',
    },

    heroHeader: {
        position: 'relative',
        zIndex: 1,
        marginBottom: theme.spacing(3),
        flex: 1,
        minWidth: 0,
    },

    heroHeaderRow: {
        position: 'relative',
        zIndex: 1,
        marginBottom: theme.spacing(3),
        display: 'flex',
        gap: theme.spacing(2.5),
        alignItems: 'flex-start',
    },

    heroImageWrap: {
        flexShrink: 0,
        width: 88,
        height: 88,
        borderRadius: 12,
        overflow: 'hidden',
        border: '2px solid rgba(255,255,255,0.28)',
        background: 'rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    heroImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },

    heroImgPlaceholder: {
        width: 42,
        height: 42,
        opacity: 0.5,
    },

    heroEyebrow: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        opacity: 0.88,
        marginBottom: theme.spacing(1.5),
    },

    heroTitle: {
        fontFamily: theme.font.primary,
        fontSize: 34,
        lineHeight: 1.05,
        fontWeight: 700,
        marginBottom: theme.spacing(1),
        [theme.down('desktop')]: {
            fontSize: 26,
        },
    },

    heroBadgeRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1.5),
    },

    heroBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: 'rgba(255, 255, 255, 0.18)',
        border: '1px solid rgba(255, 255, 255, 0.24)',
        backdropFilter: 'blur(8px)',
        letterSpacing: '0.02em',
    },

    heroMetaGrid: {
        position: 'relative',
        zIndex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.down('desktop')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [theme.down('tablet')]: {
            gridTemplateColumns: '1fr',
        },
    },

    heroMetaCard: {
        background: 'rgba(255, 255, 255, 0.12)',
        border: '1px solid rgba(255, 255, 255, 0.16)',
        borderRadius: theme.radius,
        padding: theme.spacing(3),
        backdropFilter: 'blur(14px)',
    },

    heroMetaLabel: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        opacity: 0.8,
        marginBottom: theme.spacing(1),
    },

    heroMetaValue: {
        fontSize: 26,
        fontWeight: 700,
        lineHeight: 1.1,
        marginBottom: theme.spacing(0.75),
        color: '#ffffff',
    },

    heroMetaHint: {
        fontSize: 13,
        lineHeight: 1.5,
        opacity: 0.82,
    },

    controlsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        alignItems: 'center',
        justifyContent: 'space-between',
        [theme.down('desktop')]: {
            alignItems: 'stretch',
            flexDirection: 'column',
        },
    },

    rangeControls: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        alignItems: 'center',
    },

    rangeButton: {
        border: 'none',
        background: theme.colors.white,
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
        fontSize: 13,
        fontWeight: 700,
        borderRadius: 999,
        padding: `${theme.spacing(1.8)} ${theme.spacing(2.8)}`,
        boxShadow: '0 8px 24px rgba(39, 31, 77, 0.08)',
        cursor: 'pointer',
        transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease, color 160ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 12px 28px rgba(39, 31, 77, 0.12)',
        },
        '&[data-active="true"]': {
            background: theme.colors.violet_primary,
            color: theme.colors.white,
        },
    },

    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.down('desktop')]: {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        },
        [theme.down('tablet')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [theme.down('mobile')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: theme.spacing(1.5),
        },
    },

    summaryCard: {
        background: theme.colors.surface_elevated,
        borderRadius: theme.radius,
        padding: theme.spacing(3),
        border: `1px solid ${theme.colors.grey_light}`,
        boxShadow: theme.isDark
            ? '0 4px 16px rgba(0,0,0,0.32)'
            : '0 16px 32px rgba(39, 31, 77, 0.06)',
    },

    summaryLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.colors.grey_text,
        marginBottom: theme.spacing(1.2),
    },

    summaryValue: {
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1.1,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        marginBottom: theme.spacing(1),
    },

    summaryHint: {
        fontSize: 13,
        lineHeight: 1.5,
        color: theme.colors.grey_text,
    },

    ongoingStrip: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: theme.spacing(2),
    },

    ongoingCard: {
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: theme.radius,
        background: `linear-gradient(180deg, ${theme.colors.white} 0%, ${theme.colors.surface_elevated} 100%)`,
        padding: theme.spacing(2.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
        boxShadow: '0 10px 24px rgba(39, 31, 77, 0.05)',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.isDark ? '0 16px 28px rgba(0, 0, 0, 0.45)' : '0 16px 28px rgba(39, 31, 77, 0.1)',
            borderColor: theme.colors.blue_primary,
        },
    },

    ongoingCardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        minWidth: 0,
    },

    ongoingTitle: {
        fontSize: 15,
        lineHeight: 1.35,
        fontWeight: 700,
        color: theme.colors.black,
        minWidth: 0,
    },

    ongoingBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        color: theme.colors.indigo_primary,
        background: 'rgba(99, 102, 241, 0.14)',
        whiteSpace: 'nowrap',
    },

    ongoingMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        fontSize: 12,
        lineHeight: 1.5,
        color: theme.colors.grey_text,
    },

    dashboardGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.2fr) minmax(320px, 1fr)',
        gap: theme.spacing(3),
        [theme.down('desktop')]: {
            gridTemplateColumns: '1fr',
        },
    },

    panel: {
        background: theme.colors.white,
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        boxShadow: '0 16px 34px rgba(39, 31, 77, 0.06)',
        overflow: 'hidden',
    },

    panelFull: {
        background: theme.colors.white,
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        boxShadow: '0 16px 34px rgba(39, 31, 77, 0.06)',
        overflow: 'hidden',
    },

    panelHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(3),
        borderBottom: `1px solid ${theme.colors.grey_light}`,
    },

    panelTitle: {
        fontSize: 18,
        fontWeight: 700,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
    },

    panelSubtitle: {
        fontSize: 13,
        lineHeight: 1.5,
        color: theme.colors.grey_text,
        marginTop: theme.spacing(0.6),
    },

    panelBody: {
        padding: theme.spacing(3),
    },

    healthList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },

    healthRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(72px, 120px) minmax(0, 1fr) 56px',
        gap: theme.spacing(2),
        alignItems: 'center',
    },

    healthLabel: {
        fontSize: 14,
        color: theme.colors.black,
        fontWeight: 600,
    },

    track: {
        position: 'relative',
        height: 12,
        borderRadius: 999,
        background: theme.colors.grey_lighter,
        overflow: 'hidden',
    },

    fill: {
        height: '100%',
        borderRadius: 999,
    },

    healthValue: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.colors.grey_text,
        textAlign: 'right',
    },

    activityBars: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(22px, 1fr))',
        gap: theme.spacing(1.2),
        alignItems: 'end',
        minHeight: 180,
    },

    activityBarColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.5),
    },

    activityBarValue: {
        width: '100%',
        borderRadius: '12px 12px 4px 4px',
        minHeight: 12,
        background: `linear-gradient(180deg, ${theme.colors.blue_neon} 0%, ${theme.colors.violet_primary} 100%)`,
        boxShadow: '0 10px 18px rgba(61, 92, 240, 0.18)',
    },

    activityBarStacked: {
        width: '100%',
        borderRadius: '6px 6px 4px 4px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column-reverse',
    },

    activityBarSegment: {
        width: '100%',
        minHeight: 2,
        transition: 'height 200ms ease',
    },

    pulseLegend: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${theme.spacing(0.8)} ${theme.spacing(2)}`,
        marginTop: theme.spacing(2),
    },

    pulseLegendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.8),
        fontSize: 11,
        color: theme.colors.grey_text,
        fontWeight: 600,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        transition: 'opacity 180ms ease',
        '&:hover': { opacity: '0.75 !important' },
    },

    pulseLegendCtrl: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginLeft: 'auto',
        flexShrink: 0,
    },

    pulseLegendCtrlBtn: {
        background: 'none',
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: 4,
        padding: '2px 7px',
        fontSize: 10,
        fontWeight: 700,
        color: theme.colors.grey_text,
        cursor: 'pointer',
        lineHeight: 1.5,
        letterSpacing: '0.03em',
        transition: 'all 120ms ease',
        '&:hover': {
            borderColor: theme.colors.blue_primary,
            color: theme.colors.blue_primary,
        },
    },

    pulseLegendDot: {
        width: 10,
        height: 10,
        borderRadius: 999,
        flexShrink: 0,
    },

    pulseToggle: {
        flexShrink: 0,
        border: `1.5px solid ${theme.colors.grey_light}`,
        borderRadius: 999,
        background: theme.colors.white,
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
        fontSize: 11,
        fontWeight: 700,
        padding: `3px 10px`,
        cursor: 'pointer',
        letterSpacing: '0.04em',
        transition: 'background 140ms ease, color 140ms ease, border-color 140ms ease',
        whiteSpace: 'nowrap',
        '&[data-active="true"]': {
            background: theme.colors.violet_primary,
            color: theme.colors.white,
            borderColor: theme.colors.violet_primary,
        },
        '&:not([data-active="true"]):hover': {
            borderColor: theme.colors.violet_primary,
            color: theme.colors.violet_primary,
        },
    },

    activityBarLabel: {
        fontSize: 11,
        color: theme.colors.grey_text,
    },

    activityBarCount: {
        fontSize: 11,
        fontWeight: 700,
        color: theme.colors.black,
        lineHeight: 1,
    },

    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },

    listRowInteractive: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 72px',
        gap: theme.spacing(2),
        alignItems: 'center',
        borderRadius: theme.radius,
        padding: `${theme.spacing(1.5)} ${theme.spacing(1.5)}`,
        cursor: 'pointer',
        transition: 'transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.isDark ? '0 4px 14px rgba(0, 0, 0, 0.4)' : '0 4px 8px rgba(39, 31, 77, 0.12)',
            backgroundColor: theme.isDark ? 'rgba(99, 102, 241, 0.14)' : theme.colors.surface_elevated,
        },
    },

    listMain: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },

    listTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: theme.colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

    listMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        fontSize: 12,
        color: theme.colors.grey_text,
    },

    listCount: {
        fontSize: 22,
        fontWeight: 700,
        textAlign: 'right',
        color: theme.colors.black,
        fontFamily: theme.font.primary,
    },

    actorAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: theme.colors.transp_violet,
        color: theme.colors.violet_primary,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
        marginRight: theme.spacing(1.5),
    },

    actorRow: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
    },

    linkButton: {
        border: 'none',
        background: 'none',
        padding: 0,
        textAlign: 'left',
        cursor: 'pointer',
        color: theme.colors.violet_primary,
    },

    roleBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 999,
        background: 'rgba(59, 130, 246, 0.12)',
        color: theme.colors.blue_primary,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.03em',
    },

    issueGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: theme.spacing(3),
        [theme.down('desktop')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [theme.down('tablet')]: {
            gridTemplateColumns: '1fr',
        },
    },

    issueColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        background: theme.colors.surface_elevated,
    },

    issueColumnHeader: {
        paddingBottom: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.colors.grey_light}`,
    },

    issueTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: theme.colors.black,
        marginBottom: theme.spacing(1.5),
    },

    issueList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },

    issueRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
    },

    issueName: {
        fontSize: 13,
        color: theme.colors.black,
        fontWeight: 600,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

    issueValue: {
        fontSize: 12,
        color: theme.colors.grey_text,
        fontWeight: 700,
        textAlign: 'right',
        whiteSpace: 'nowrap',
    },

    supportText: {
        fontSize: 14,
        lineHeight: 1.6,
        color: theme.colors.grey_text,
    },

    chipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
    },

    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1.2),
        padding: '4px 12px',
        borderRadius: 999,
        background: theme.colors.transp_violet,
        color: theme.colors.violet_primary,
        fontSize: 12,
        fontWeight: 700,
    },

    chipValue: {
        color: theme.colors.black,
    },

    mixSection: {
        marginBottom: theme.spacing(3.5),
        '&:last-child': {
            marginBottom: 0,
        },
    },

    mixSectionTitle: {
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.colors.grey_text,
        marginBottom: theme.spacing(1.5),
    },

    mixRow: {
        display: 'none', // superseded by mixCard layout
    },

    mixLabelCell: {
        display: 'none',
    },

    mixLabel: {
        display: 'none',
    },

    mixShareBadge: {
        display: 'none',
    },

    mixBarTrack: {
        display: 'none',
    },

    mixBarFill: {
        display: 'none',
    },

    mixRightStats: {
        display: 'none',
    },

    mixCompletionPct: {
        display: 'none',
    },

    mixCountLine: {
        display: 'none',
    },

    mixCardGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: theme.spacing(1.5),
    },

    mixCard: {
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        background: theme.colors.white,
        padding: `${theme.spacing(1.75)} ${theme.spacing(2)}`,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },

    mixCardHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },

    mixCardLabel: {
        fontSize: 14,
        fontWeight: 700,
        color: theme.colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontFamily: theme.font.secondary,
    },

    mixCardMetaGroup: {
        display: 'flex',
        gap: theme.spacing(0.5),
        flexWrap: 'wrap',
    },

    mixCardMetaChip: {
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 500,
        color: theme.colors.grey_text,
        background: theme.colors.grey_light,
        borderRadius: 999,
        padding: '2px 7px',
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        border: 'none',
        outline: 'none',
        transition: 'background 150ms, color 150ms',
        '&:hover': {
            background: theme.colors.transp_violet,
            color: theme.colors.violet_primary,
        },
    },

    mixCardMetaChipActive: {
        background: theme.colors.transp_violet,
        color: theme.colors.violet_primary,
        fontWeight: 600,
    },

    chipPanel: {
        marginTop: theme.spacing(0.5),
        padding: theme.spacing(1.5),
        borderRadius: theme.radius,
        background: theme.colors.grey_background,
        border: `1px solid ${theme.colors.grey_light}`,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },

    chipPanelTitle: {
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: theme.colors.grey_text,
    },

    chipPanelEmpty: {
        fontSize: 12,
        color: theme.colors.grey_text,
        fontStyle: 'italic',
    },

    chipBarChart: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 3,
        height: 64,
        paddingTop: theme.spacing(0.5),
    },

    chipBarCol: {
        flex: '1 1 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        minWidth: 0,
        height: '100%',
    },

    chipBarColTrack: {
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        borderRadius: '3px 3px 0 0',
        overflow: 'hidden',
    },

    chipBarColFill: {
        background: theme.colors.violet_primary,
        borderRadius: '3px 3px 0 0',
        opacity: 0.75,
        transition: 'height 300ms ease',
        minHeight: 2,
        width: '100%',
    },

    chipBarColLabel: {
        fontSize: 9,
        color: theme.colors.grey_text,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
        lineHeight: 1.2,
        textAlign: 'center',
    },

    chipBarColValue: {
        fontSize: 9,
        fontWeight: 700,
        color: theme.colors.black,
        lineHeight: 1,
    },

    chipActorList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        maxHeight: 140,
        overflowY: 'auto',
    },

    chipActorRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: `2px 0`,
    },

    chipActorName: {
        fontSize: 12,
        fontWeight: 500,
        color: theme.colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },

    chipActorCount: {
        fontSize: 11,
        color: theme.colors.grey_text,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },

    chipDurationStats: {
        display: 'flex',
        gap: theme.spacing(2),
    },

    chipDurationStat: {
        fontSize: 12,
        color: theme.colors.black,
        fontWeight: 600,
    },

    chipDurationStatLabel: {
        fontSize: 10,
        fontWeight: 500,
        color: theme.colors.grey_text,
        marginRight: 3,
    },

    mixCardStatusRows: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },

    mixStatusRow: {
        display: 'grid',
        gridTemplateColumns: '8px 76px 1fr 26px',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },

    mixStatusDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        flexShrink: 0,
    },

    mixStatusLabel: {
        fontSize: 12,
        fontWeight: 500,
        color: theme.colors.grey_text,
        lineHeight: 1,
        whiteSpace: 'nowrap',
    },

    mixStatusBarTrack: {
        height: 10,
        borderRadius: 999,
        background: theme.colors.grey_light,
        overflow: 'hidden',
    },

    mixStatusBarFill: {
        height: '100%',
        borderRadius: 999,
        transition: 'width 420ms cubic-bezier(0.4, 0, 0.2, 1)',
    },

    mixStatusCount: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.colors.black,
        textAlign: 'right',
        lineHeight: 1,
    },

    // legacy — superseded
    mixCardStats: { display: 'none' },
    mixCardStat: { display: 'none' },
    mixCardValue: { display: 'none' },
    mixCardKey: { display: 'none' },
    mixCardStrip: { display: 'none' },

    mixEmptyState: {
        fontSize: 13,
        color: theme.colors.grey_text,
        fontStyle: 'italic',
        display: 'block',
        paddingBottom: theme.spacing(1),
    },

    recentGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.down('tablet')]: {
            gridTemplateColumns: '1fr',
        },
    },

    recentCardInteractive: {
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.grey_light}`,
        padding: theme.spacing(3),
        background: `linear-gradient(180deg, ${theme.colors.white} 0%, ${theme.colors.surface_elevated} 100%)`,
        cursor: 'pointer',
        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 12px 24px rgba(39, 31, 77, 0.1)',
            borderColor: theme.colors.violet_primary,
        },
    },

    recentTitle: {
        display: 'block',
        fontSize: 14,
        fontWeight: 700,
        color: theme.colors.black,
        marginBottom: theme.spacing(1.5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

    recentMetaChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },

    recentMetaChip: {
        display: 'inline-flex',
        padding: '2px 8px',
        borderRadius: 999,
        background: theme.colors.grey_lighter,
        color: theme.colors.grey_text,
        fontSize: 11,
        fontWeight: 600,
    },

    statusPill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.03em',
    },

    errorCard: {
        borderRadius: theme.radius,
        background: 'rgba(220, 38, 38, 0.08)',
        border: `1px solid rgba(220, 38, 38, 0.2)`,
        padding: theme.spacing(4),
        color: theme.colors.red,
        fontWeight: 600,
        fontSize: 14,
    },

    emptyState: {
        borderRadius: theme.radius,
        background: theme.colors.surface_elevated,
        border: `1px solid ${theme.colors.grey_light}`,
        padding: theme.spacing(6),
        textAlign: 'center',
    },

    emptyTitle: {
        fontSize: 20,
        fontWeight: 700,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        marginBottom: theme.spacing(1.5),
    },

    emptyDescription: {
        fontSize: 14,
        lineHeight: 1.6,
        color: theme.colors.grey_text,
        maxWidth: 480,
        margin: '0 auto',
    },

    /* ── Chip detail popover ── */
    chipPopoverBackdrop: {
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        // transparent – only catches click-outside
    },

    chipPopoverBox: {
        // Viewport-space positioning: the anchor is captured in viewport
        // coordinates, so `fixed` keeps the popup aligned to the bar regardless
        // of which container scrolls.
        position: 'fixed',
        zIndex: 1301,
        background: theme.colors.white,
        borderRadius: 12,
        boxShadow: theme.isDark
            ? '0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.07)'
            : '0 8px 32px rgba(39,31,77,0.15), 0 0 0 1px rgba(39,31,77,0.07)',
        maxHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // width is set inline
        // On phones the anchored bubble is unusable; dock it as a bottom sheet.
        [theme.down('mobile')]: {
            top: 'auto !important',
            left: '0 !important',
            right: 0,
            bottom: 0,
            width: 'auto !important',
            maxWidth: '100%',
            maxHeight: '80vh',
            borderRadius: '16px 16px 0 0',
        },
    },

    chipPopoverArrow: {
        position: 'absolute',
        top: -7,
        width: 14,
        height: 7,
        overflow: 'hidden',
        // left set inline
        [theme.down('mobile')]: {
            display: 'none',
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: 14,
            height: 14,
            background: theme.colors.white,
            boxShadow: theme.isDark
                ? '-2px -2px 4px rgba(0,0,0,0.4)'
                : '-2px -2px 4px rgba(39,31,77,0.1)',
            transform: 'rotate(45deg)',
            transformOrigin: 'center center',
        },
    },

    // Side arrow: popup is to the RIGHT of the bar, arrow points left toward bar
    pulseBarArrowLeft: {
        position: 'absolute',
        left: -7,
        width: 7,
        height: 14,
        overflow: 'hidden',
        // top set inline
        [theme.down('mobile')]: {
            display: 'none',
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            width: 14,
            height: 14,
            background: theme.colors.white,
            boxShadow: theme.isDark
                ? '-2px -2px 4px rgba(0,0,0,0.4)'
                : '-2px -2px 4px rgba(39,31,77,0.1)',
            transform: 'rotate(45deg)',
            transformOrigin: 'center center',
        },
    },

    // Side arrow: popup is to the LEFT of the bar, arrow points right toward bar
    pulseBarArrowRight: {
        position: 'absolute',
        right: -7,
        width: 7,
        height: 14,
        overflow: 'hidden',
        // top set inline
        [theme.down('mobile')]: {
            display: 'none',
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            width: 14,
            height: 14,
            background: theme.colors.white,
            boxShadow: theme.isDark
                ? '2px -2px 4px rgba(0,0,0,0.4)'
                : '2px -2px 4px rgba(39,31,77,0.1)',
            transform: 'rotate(45deg)',
            transformOrigin: 'center center',
        },
    },

    chipModalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
        borderBottom: `1px solid ${theme.colors.grey_light}`,
        flexShrink: 0,
    },

    chipModalTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
    },

    chipModalSubtitle: {
        fontSize: 11,
        color: theme.colors.grey_text,
        marginTop: 2,
    },

    chipModalClose: {
        background: 'none',
        border: 'none',
        fontSize: 14,
        lineHeight: 1,
        cursor: 'pointer',
        color: theme.colors.grey_text,
        padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
        borderRadius: 4,
        transition: 'background 0.15s',
        '&:hover': {
            background: theme.colors.grey_light,
        },
    },

    chipModalBody: {
        padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
        overflowY: 'auto',
        flex: 1,
    },

    chipModalBarChart: {
        display: 'flex',
        gap: 4,
        alignItems: 'flex-end',
        height: 120,
        padding: `${theme.spacing(0.5)} 0`,
    },

    chipModalBarCol: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        height: '100%',
        justifyContent: 'flex-end',
    },

    chipModalBarColValue: {
        fontSize: 10,
        color: theme.colors.grey_text,
        lineHeight: 1,
        minHeight: 13,
    },

    chipModalBarColTrack: {
        flex: 1,
        width: '100%',
        background: theme.colors.grey_light,
        borderRadius: '4px 4px 0 0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
    },

    chipModalBarColFill: {
        width: '100%',
        background: theme.colors.blue_primary,
        borderRadius: '4px 4px 0 0',
        transition: 'height 0.3s ease',
        minHeight: 3,
    },

    chipModalBarColLabel: {
        fontSize: 9,
        color: theme.colors.grey_text,
        textAlign: 'center',
        lineHeight: 1.2,
        wordBreak: 'break-word',
        maxWidth: '100%',
        marginTop: 2,
    },

    chipModalActorList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },

    chipModalActorRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        padding: `${theme.spacing(0.75)} 0`,
        borderBottom: `1px solid ${theme.colors.grey_light}`,
        '&:last-child': { borderBottom: 'none' },
    },

    chipModalActorAvatar: {
        flexShrink: 0,
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: (theme.colors as any).blue_light ?? theme.colors.grey_light,
        color: theme.colors.blue_primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
    },

    chipModalActorName: {
        flex: 1,
        fontSize: 13,
        fontWeight: 500,
        color: theme.colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

    chipModalActorCount: {
        fontSize: 12,
        color: theme.colors.grey_text,
        flexShrink: 0,
    },

    chipModalDurationStats: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },

    chipModalDurationCard: {
        flex: 1,
        background: theme.colors.grey_light,
        borderRadius: 8,
        padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        alignItems: 'center',
    },

    chipModalDurationLabel: {
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.colors.grey_text,
    },

    chipModalDurationValue: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.colors.black,
    },

    /* ── Pulse-bar detail popup ── */
    pulseBarPopupAccent: {
        height: 4,
        flexShrink: 0,
        borderRadius: '12px 12px 0 0',
        background: `linear-gradient(90deg, ${theme.colors.violet_primary} 0%, ${theme.colors.blue_neon} 100%)`,
    },

    pulseBarPopupHeaderContent: {
        padding: `${theme.spacing(1.5)} ${theme.spacing(2)} ${theme.spacing(1.25)} ${theme.spacing(2)}`,
        borderBottom: `1px solid ${theme.colors.grey_light}`,
        flexShrink: 0,
    },

    pulseBarPopupHeaderTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },

    pulseBarPopupDate: {
        fontSize: 14,
        fontWeight: 800,
        color: theme.colors.black,
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
    },

    pulseBarPopupCount: {
        fontSize: 11,
        fontWeight: 500,
        color: theme.colors.grey_text,
        marginTop: 3,
        lineHeight: 1.2,
    },

    pulseBarPopupSummaryRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
        marginTop: theme.spacing(1),
    },

    pulseBarPopupSummaryPill: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        color: theme.colors.grey_text,
        lineHeight: 1,
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        transition: 'opacity 180ms ease',
    },

    pulseBarPopupSummaryDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        flexShrink: 0,
    },

    pulseBarPopupScroller: {
        overflowY: 'auto',
        flex: 1,
        padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
        /* custom thin scrollbar */
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
            background: theme.colors.grey_light,
            borderRadius: 4,
        },
    },

    pulseBarPopupList: {
        display: 'flex',
        flexDirection: 'column',
    },

    pulseBarPopupRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
        borderBottom: `1px solid ${theme.colors.grey_light}`,
        '&:last-child': { borderBottom: 'none' },
        cursor: 'pointer',
        borderRadius: 8,
        transition: 'background 120ms ease',
        '&:hover': {
            background: theme.isDark
                ? 'rgba(108,52,239,0.13)'
                : 'rgba(108,52,239,0.07)',
        },
        '&:active': {
            background: theme.isDark
                ? 'rgba(108,52,239,0.22)'
                : 'rgba(108,52,239,0.14)',
        },
    },

    pulseBarPopupAvatar: {
        flexShrink: 0,
        width: 34,
        height: 34,
        borderRadius: 9,
        background: `linear-gradient(135deg, ${theme.colors.violet_primary} 0%, ${theme.colors.blue_neon} 100%)`,
        color: theme.colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.02em',
    },

    pulseBarPopupRowMain: {
        flex: 1,
        minWidth: 0,
    },

    pulseBarPopupName: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.3,
    },

    pulseBarPopupRowSub: {
        fontSize: 10,
        color: theme.colors.grey_text,
        marginTop: 2,
        lineHeight: 1.2,
    },

    pulseBarPopupBadges: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 4,
    },

    pulseBarPopupBadge: {
        flexShrink: 0,
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
    },
}));
